import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconInfoCircle,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { useState } from "react";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import {
  isAhamoveNeedForce,
  pushAhamoveOrder,
} from "../../../api/apiDeliveryPartner";

// Thay cho ConfirmInfoAhamove: FE chỉ gửi mã đơn, BE tự dựng địa chỉ và gọi Ahamove
const PushAhamoveOrder = ({
  orderNumber,
  customerName,
  phone,
  branch,
  shippingVendor,
  typeShip,
  existingAhamove,
  onSuccess,
}: {
  orderNumber: string;
  customerName?: string;
  phone?: string;
  branch?: string;
  shippingVendor?: string;
  typeShip?: string;
  existingAhamove?: string | null; // sharedLink / ahamoveOrderId đang có trong attribute1
  onSuccess: (message?: string) => void; // nơi gọi tải lại danh sách rồi mới hiện message
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  // Có giá trị khi phải hỏi xác nhận tạo lại đơn Ahamove
  const [forceMessage, setForceMessage] = useState<string | null>(
    existingAhamove ? `Đơn đã có đơn Ahamove (${existingAhamove}).` : null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Ahamove có thể đã tạo đơn nên không cho bấm lại ngay
  const [locked, setLocked] = useState<boolean>(false);

  const infoRows = [
    {
      label: "Khách hàng",
      value: [customerName, phone].filter(Boolean).join(" - "),
    },
    { label: "Kho hàng", value: branch },
    { label: "Hình thức", value: typeShip },
  ].filter((i) => i.value);

  const handlePush = async () => {
    setLoading(true);
    setErrorMessage(null);
    const res = await pushAhamoveOrder({ orderNumber, force: !!forceMessage });
    setLoading(false);
    if (!res) return;

    if (res.success) {
      modals.closeAll();
      onSuccess(res.message);
      return;
    }

    NotificationExtension.Fails(res.message);
    if (isAhamoveNeedForce(res.message)) {
      setForceMessage(res.message);
    } else if (res.message?.startsWith("Đơn đang được đẩy sang Ahamove")) {
      modals.closeAll();
      setTimeout(onSuccess, 3000);
    } else {
      setErrorMessage(res.message);
      setLocked(res.message?.startsWith("Chưa rõ kết quả tạo đơn Ahamove"));
    }
  };

  return (
    <Box>
      <Stack gap="md" mt={10}>
        <Paper withBorder radius="md" p="md" bg="gray.0">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Text fw={700} size="lg">
              {orderNumber}
            </Text>
            {shippingVendor && (
              <Badge variant="light" color="cyan" style={{ flexShrink: 1 }}>
                {shippingVendor}
              </Badge>
            )}
          </Group>
          {infoRows.length > 0 && (
            <Stack gap={6} mt="sm">
              {infoRows.map((row) => (
                <Group
                  key={row.label}
                  gap="sm"
                  wrap="nowrap"
                  align="baseline"
                >
                  <Text size="sm" c="dimmed" w={80} style={{ flexShrink: 0 }}>
                    {row.label}
                  </Text>
                  <Text size="md">{row.value}</Text>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>

        {forceMessage ? (
          <Alert
            variant="light"
            color="orange"
            icon={<IconAlertTriangle size={18} />}
            title="Đơn có thể đã có đơn Ahamove"
          >
            <Text size="sm">{forceMessage}</Text>
            <Text size="sm" fw={600} mt={6}>
              Chỉ tạo lại khi đã kiểm tra trên Ahamove: đơn cũ đã huỷ hoặc chưa
              có đơn.
            </Text>
          </Alert>
        ) : (
          <Group gap={6} wrap="nowrap" align="flex-start">
            <IconInfoCircle
              size={16}
              color="var(--mantine-color-dimmed)"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Text size="sm" c="dimmed">
              Địa chỉ giao do hệ thống lấy từ đơn hàng. Quá trình có thể mất tới
              30 giây.
            </Text>
          </Group>
        )}

        {errorMessage && (
          <Alert
            variant="light"
            color="red"
            icon={<IconAlertCircle size={18} />}
            title={locked ? "Chưa rõ kết quả" : "Đẩy đơn chưa thành công"}
          >
            <Text size="sm">{errorMessage}</Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => modals.closeAll()}>
            Đóng
          </Button>
          <Button
            variant="filled"
            color={forceMessage ? "orange" : "cyan"}
            leftSection={<IconTruckDelivery size={16} />}
            loading={loading}
            disabled={locked}
            onClick={handlePush}
          >
            {forceMessage ? "Tạo lại đơn" : "Đẩy đơn"}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};

export default PushAhamoveOrder;
